import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Grid,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WebIcon from '@mui/icons-material/Web';
import BusinessIcon from '@mui/icons-material/Business';
import ArticleIcon from '@mui/icons-material/Article';
import StorageIcon from '@mui/icons-material/Storage';
import AppsIcon from '@mui/icons-material/Apps';
import { CampaignProspectType } from '../../api/campaigns';
import { useUserContextController } from '../../context/UserContext';
import HubSpotLogo from '../../assets/hubspot-96.png';
import SalesforceLogo from '../../assets/SalesforceLogo.svg';
import ImportOptionCard, { ImportOption } from './ImportOptionCard';

interface ImportProspectsModalProps {
  open: boolean;
  onClose: () => void;
  prospectType: CampaignProspectType;
  onOptionSelect: (option: string) => void;
}

const ImportProspectsModal: React.FC<ImportProspectsModalProps> = ({
  open,
  onClose,
  prospectType,
  onOptionSelect
}) => {
  const theme = useTheme();
  const [userContext] = useUserContextController();

  const hasSalesforce = !!userContext?.connectedAccounts?.some(
    (a) => a.app === 'salesforce_rest_api' && !!a.connectionId
  );
  const hasHubspot = !!userContext?.connectedAccounts?.some(
    (a) => a.app === 'hubspot' && !!a.connectionId
  );

  const hasAnyCRM = hasSalesforce || hasHubspot;
  const isAccountCampaign = prospectType === CampaignProspectType.ACCOUNT;
  const importCardAccent = isAccountCampaign
    ? theme.customColors.spreadsheetAccountAccent
    : theme.customColors.themePrimary;

  const handleOptionClick = (option: string) => {
    onOptionSelect(option);
    onClose();
  };

  const getCRMLogos = () => {
    const logos = [];
    if (hasSalesforce) logos.push({ src: SalesforceLogo, alt: 'Salesforce' });
    if (hasHubspot) logos.push({ src: HubSpotLogo, alt: 'HubSpot' });
    return logos;
  };

  const importOptions: ImportOption[] = [
    {
      id: 'upload_csv',
      label: 'Upload CSV',
      icon: <UploadFileIcon sx={{ fontSize: '32px' }} />,
      description: 'Import from a CSV file'
    },
    {
      id: 'scrape_website',
      label: 'Scrape Website',
      icon: <WebIcon sx={{ fontSize: '32px' }} />,
      description: 'Extract data from websites'
    },
    {
      id: 'import_crm',
      label: 'Your CRM',
      icon: <AppsIcon sx={{ fontSize: '32px' }} />,
      description: 'Import from your CRM',
      disabled: !hasAnyCRM,
      tooltip: hasAnyCRM
        ? undefined
        : 'Connect your CRM in Settings → Integrations. Contact your admin if you don’t have access.'
    } as ImportOption,
    ...(isAccountCampaign
      ? [
          {
            id: 'search_and_enrich',
            label: 'Prospect database',
            icon: <StorageIcon sx={{ fontSize: '32px' }} />,
            description: 'Search prospects from Dokai Database'
          } as ImportOption
        ]
      : []),
    ...(isAccountCampaign
      ? [
          {
            id: 'search_registries',
            label: 'SEC Registries',
            icon: <BusinessIcon sx={{ fontSize: '32px' }} />,
            description: 'Search SEC company registries',
            disabled: true,
            tooltip: 'Coming soon'
          } as ImportOption,
          {
            id: 'search_news',
            label: 'Search News',
            icon: <ArticleIcon sx={{ fontSize: '32px' }} />,
            description: 'Find companies in news articles',
            disabled: true,
            tooltip: 'Coming soon'
          } as ImportOption
        ]
      : [])
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: '24px',
            color: (theme) => theme.customColors?.themeNeutralPrimaryColor
          }}
        >
          Import Prospects
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: (theme) => theme.customColors?.themeNeutralSecondaryColor,
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 2 }}>
        <Grid container spacing={2} sx={{ pt: 2 }}>
          {importOptions.map((option) => (
            <Grid item xs={6} sm={4} key={option.id}>
              <ImportOptionCard
                option={option}
                onClick={handleOptionClick}
                crmLogos={getCRMLogos()}
                showCRMLogos={option.id === 'import_crm' && hasAnyCRM}
                interactionAccent={importCardAccent}
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default ImportProspectsModal;
